(function AdzukiGoogleMap($, Adzuki)
{
	'use strict';

	const component = {
		props: {
			divName: {
				type    : String,
				required: true,
			},
			mapInfo: {
				type    : Array,
				required: true,
			},
			showMap: {
				type    : Boolean,
				required: true,
				default : false,
			},
			mapTitle: {
				type    : String,
				required: false,
			},
			hasPoi: {
				type    : Boolean,
				required: true,
				default : false,
			},
			coordinates: {
				type    : Object,
				required: false,
				validator(value, rawProps) {
					if (rawProps.hasPoi && !value) {
						console.warn('Coordinates data is required when showing points of interest');
						return false;
					}
					return true;
				}
			},
			radius: {
				type    : Number,
				required: false,
				validator(value, rawProps) {
					if (rawProps.hasPoi && !value) {
						console.warn('A Radius is required when showing points of interest');
						return false;
					}
					return true;
				}
			},
			showPriceMarker: {
				type   : Boolean,
				default: true,
			},
		},
		template: `
			<div v-show="showMap" class="hotel-map-view">
				<div class="hotel-map-header margin10-bottom">
					<div class="hotel-map-header-main">
						<h3 class="hotel-results-count hotel-map-count">
							<span class="hotel-count-number" v-if="!hasPoi && mapTitle">{{ mapTitle }}</span>
							<span class="hotel-count-number" v-if="hasPoi">{{ localMapInfo.length }} markers found <span v-if="mapTitle">near {{ mapTitle }}</span></span>
						</h3>
					</div>
					<div class="hotel-map-header-poi" v-if="showPoi">
						<h3>Points of Interest</h3>
					</div>
				</div>
				<div class="hotel-map-content">
					<div class="hotel-map-panel">
						<button
							v-if="fullscreenSupported"
							type="button"
							class="btn btn-default btn-sm hotel-map-fullscreen-btn"
							:title="isMapFullscreen ? 'Exit full screen' : 'Full screen'"
							:aria-label="isMapFullscreen ? 'Exit full screen' : 'Full screen'"
							@click="toggleMapFullscreen"
						>
							<i :class="['fas', isMapFullscreen ? 'fa-compress' : 'fa-expand']" aria-hidden="true"></i>
						</button>
						<div :class="divName" class="hotel-results-map"></div>
					</div>
					<div class="poi-list" v-if="showPoi && pointsOfInterest && pointsOfInterest.pois">
						<div class="poi-panel" v-for="(pois, category) in pointsOfInterest.pois" :key="category">
							<div class="poi-panel-header" :id="'heading-'+category">
								<label class="poi-select-all-label">
									<input
										type="checkbox"
										class="poi-category-checkbox"
										:id="'select-all-'+category"
										:checked="getCategoryCheckState(category).checked"
										:ref="'catCheck_' + category"
										@change="(e) => selectAllInCategory(category, e.target.checked)"
									/>
								</label>
								<span class="poi-category-name" v-html="category"></span>
								<span class="poi-category-count">({{ pois.length }})</span>
								<span class="poi-toggle" @click="showCategory(category)">
									<i :class="['fas', activePoiCat === category ? 'fa-minus-square' : 'fa-plus-square']"></i>
								</span>
							</div>
							<div class="poi-panel-body" v-show="activePoiCat === category">
								<ul class="list-group list-group-flush">
									<li v-for="poi in pois" :key="poi.id" class="list-group-item poi-list-item">
										<label class="poi-item-label">
											<input
												type="checkbox"
												:name="poi.name"
												:checked="isPoiSelected(poi)"
												@change="() => updatePoiInfo(poi)"
											/>
											<span class="poi-item-name">{{ poi.name }}</span>
										</label>
										<div class="poi-item-meta">
											<span
												class="poi-item-address text-muted">{{ poi.address || 'No address available' }}</span>
											<span class="badge bg-primary rounded-pill poi-item-distance">
												{{ poi.distance_mi < 1 ? '< 1 mile' : poi.distance_mi + ' mi' }}
											</span>
										</div>
									</li>
								</ul>
							</div>
						</div>
					</div>
				</div>
			</div>
		`,
		data() {
			return {
				baseUri                : Adzuki.base_uri,
				localMapInfo           : [],
				pointsOfInterest       : [],
				activePoiCat           : null,
				showPoi                : false,
				selectedPois           : new Set(),
				googleMap              : null,
				mapMarkers             : [],
				activeMarker           : null,
				isMapFullscreen        : false,
				fullscreenChangeHandler: null,
			};
		},
		computed: {
			fullscreenSupported() {
				return Boolean(Adzuki.Fullscreen && Adzuki.Fullscreen.isSupported());
			},
		},
		methods: {
			formatCurrency(value, showDecimals = true) {
				const amount = parseFloat(value);

				if (isNaN(amount)) {
					return '';
				}

				return new Intl.NumberFormat('en-US', {
					style                : 'currency',
					currency             : 'USD',
					minimumFractionDigits: showDecimals ? 2 : 0,
					maximumFractionDigits: showDecimals ? 2 : 0
				}).format(amount);
			},
			buildStarRatingHtml(starRating) {
				if (!starRating) {
					return '';
				}

				return Array.from({ length: Math.floor(starRating) }, () => '<i class="fas fa-star"></i>').join('');
			},
			hasValidPrice(price) {
				const amount = parseFloat(price);

				return !isNaN(amount) && amount > 0;
			},
			shouldShowPriceMarker(markerInfo) {
				return !markerInfo.isPoi && this.showPriceMarker && markerInfo.price;
			},
			shouldShowHotelMarker(markerInfo) {
				return !markerInfo.isPoi && !this.showPriceMarker;
			},
			isInteractiveHotelMarker(markerInfo) {
				return this.shouldShowPriceMarker(markerInfo) || this.shouldShowHotelMarker(markerInfo);
			},
			getHotelDetailsUrl(hotelId) {
				if (Adzuki.app === 'Web') {
					return `${this.baseUri}/HotelSearch/hotelDetails/${hotelId}`;
				}

				return `${this.baseUri}/HotelEngine/hotelDetails/${hotelId}`;
			},
			buildMapContent(property) {
				const content = document.createElement('div');
				content.className = 'marker-container';
				content.innerHTML = `
					<div class="price-marker" style="background-color: rgb(0, 51, 102); color: white; padding: 5px 10px; border-radius: 4px; font-weight: bold; display: inline-block; min-width: 40px; text-align: center;">${this.formatCurrency(property.price, false)}</div>
				`;

				return content;
			},
			buildHotelMarkerContent(property) {
				const content = document.createElement('div');
				content.className = 'marker-container';
				content.innerHTML = `
					<div class="hotel-marker" title="${property.title || 'Hotel'}">
						<span class="hotel-marker-pin" aria-hidden="true">
							<i class="fas fa-hotel"></i>
						</span>
					</div>
				`;

				return content;
			},
			buildMarkerContent(markerInfo) {
				if (this.shouldShowPriceMarker(markerInfo)) {
					return this.buildMapContent(markerInfo);
				}

				if (this.shouldShowHotelMarker(markerInfo)) {
					return this.buildHotelMarkerContent(markerInfo);
				}

				return null;
			},
			buildMarkerDetailsPanel(property) {
				const panel = document.createElement('div');
				panel.className = 'marker-details marker-details-overlay';

				const showPrice      = this.showPriceMarker && this.hasValidPrice(property.price);
				const showSelectLink = this.showPriceMarker && property.hotel_id;
				const priceHtml      = showPrice
					? `<p class="margin0-top margin10-bottom">
							<span class="min-rate" style="font-size: 1.2em; font-weight: bold;">${this.formatCurrency(property.price, false)}/<small> per night</small></span>
						</p>`
					: '';
				const selectHotelHtml = showSelectLink
					? `<div class="text-start pad10-vert">
							<a href="${this.getHotelDetailsUrl(property.hotel_id)}" class="go-hotel btn btn-primary">See Availability</a>
						</div>`
					: '';

				panel.innerHTML = `
					<div class="map-header-content d-flex align-items-center justify-content-between margin10-bottom" style="width: 100%;">
						<h4>${property.title}</h4>
						<span class="close-marker"><i class="fas fa-fw fa-times"></i></span>
					</div>
					<div class="map-marker-content row" style="width: 100%;">
						<div class="details-container col-sm-12">
							<div class="d-flex align-items-start justify-content-evenly gap-3">
								<div class="hotel-image">
									<img src="${property.image}" alt="${property.title}" style="width: 100%; height: auto; max-height: 120px; object-fit: cover;" class="img-fluid">
									${selectHotelHtml}
								</div>
								<div class="hotel-details">
									<div class="details">
										<p class="title" style="font-weight: bold; margin-bottom: 5px;">${property.title}</p>
										<p class="address" style="margin-bottom: 5px;">${property.address}</p>
										<div>
											<p class="margin10-bottom">${this.buildStarRatingHtml(property.star_rating)}</p>
											${priceHtml}
										</div>
									</div>
								</div>
							</div>
						</div>
					</div>
				`;

				const closeButton = panel.querySelector('.close-marker');
				if (closeButton) {
					closeButton.addEventListener('click', (e) => {
						e.stopPropagation();
						this.closeMarkerOverlay();
					});
				}

				const linkButton = panel.querySelector('.go-hotel');
				if (linkButton) {
					linkButton.addEventListener('click', (e) => {
						e.stopPropagation();
						window.location.href = linkButton.href;
					});
				}

				return panel;
			},
			ensureMapOverlay(mapContainer) {
				let mapPanel = mapContainer.closest('.hotel-map-panel');

				if (!mapPanel) {
					mapPanel = document.createElement('div');
					mapPanel.className = 'hotel-map-panel';
					mapContainer.parentNode.insertBefore(mapPanel, mapContainer);
					mapPanel.appendChild(mapContainer);
				}

				let overlay = mapPanel.querySelector(':scope > .map-marker-overlay');

				if (!overlay) {
					overlay = document.createElement('div');
					overlay.className = 'map-marker-overlay hidden';
					mapPanel.appendChild(overlay);
				}

				return overlay;
			},
			getMapContainer() {
				return this.$el ? this.$el.querySelector('.' + this.divName) : null;
			},
			getMapPanel() {
				const mapContainer = this.getMapContainer();

				if (!mapContainer) {
					return null;
				}

				this.ensureMapOverlay(mapContainer);

				return mapContainer.closest('.hotel-map-panel');
			},
			getFullscreenElement() {
				return document.fullscreenElement ||
					document.webkitFullscreenElement ||
					document.mozFullScreenElement ||
					document.msFullscreenElement ||
					null;
			},
			bindFullscreenListener() {
				if (this.fullscreenChangeHandler) {
					return;
				}

				this.fullscreenChangeHandler = () => {
					this.handleFullscreenChange();
				};

				document.addEventListener('fullscreenchange', this.fullscreenChangeHandler);
				document.addEventListener('webkitfullscreenchange', this.fullscreenChangeHandler);
			},
			unbindFullscreenListener() {
				if (!this.fullscreenChangeHandler) {
					return;
				}

				document.removeEventListener('fullscreenchange', this.fullscreenChangeHandler);
				document.removeEventListener('webkitfullscreenchange', this.fullscreenChangeHandler);
				this.fullscreenChangeHandler = null;
			},
			handleFullscreenChange() {
				const mapPanel     = this.getMapPanel();
				const fullscreenEl = this.getFullscreenElement();

				this.isMapFullscreen = Boolean(mapPanel && fullscreenEl === mapPanel);

				if (this.googleMap && (!fullscreenEl || fullscreenEl === mapPanel)) {
					google.maps.event.trigger(this.googleMap, 'resize');
				}
			},
			toggleMapFullscreen() {
				const mapPanel = this.getMapPanel();

				if (!mapPanel || !Adzuki.Fullscreen) {
					return;
				}

				Adzuki.Fullscreen.toggleFullscreen(mapPanel);
			},
			closeMarkerOverlay() {
				const mapContainer = this.getMapContainer();

				if (!mapContainer) {
					return;
				}

				if (this.activeMarker) {
					this.activeMarker.zIndex = null;
				}

				const overlay = this.ensureMapOverlay(mapContainer);
				overlay.classList.add('hidden');
				overlay.innerHTML = '';
				this.activeMarker = null;
			},
			clearMapMarkers() {
				this.mapMarkers.forEach((marker) => {
					marker.map = null;
				});

				this.mapMarkers = [];
				this.closeMarkerOverlay();
			},
			initGoogleMap(divClass) {
				const mapContainer = this.$el
					? this.$el.querySelector('.' + divClass)
					: document.querySelector('.' + divClass);

				if (!mapContainer) {
					console.error('Map container not found!');
					return;
				}

				// Parse markers from the data-map-markers attribute
				const markersData = this.localMapInfo;

				// Ensure there's at least one marker
				if (!markersData.length) {
					console.warn('No marker data available to render');
					this.clearMapMarkers();
					return;
				}

				this.mapMarkers = [];

				// Center the map on the first marker
				const centerCoordinates = {
					lat: parseFloat(markersData[0].lat),
					lng: parseFloat(markersData[0].lng),
				};

				// Initialize the Google Map
				const map = new google.maps.Map(mapContainer, {
					center           : centerCoordinates,
					zoom             : 12,
					mapId            : divClass,
					fullscreenControl: false,
				});

				this.googleMap = map;
				this.ensureMapOverlay(mapContainer);
				this.closeMarkerOverlay();

				const bounds = new google.maps.LatLngBounds();

				// Add markers to the map
				markersData.forEach((markerInfo) => {
					let marker;
					const position = {
						lat: parseFloat(markerInfo.lat),
						lng: parseFloat(markerInfo.lng)
					};

					if (markerInfo.lat && markerInfo.lng) {
						const markerContent = this.buildMarkerContent(markerInfo);

						if (markerContent) {
							marker = new google.maps.marker.AdvancedMarkerElement({
								position: position,
								content : markerContent,
								map,
								title   : markerInfo.title,
							});
						} else {
							marker = new google.maps.marker.AdvancedMarkerElement({
								position: position,
								map,
								title   : markerInfo.title
							});
						}

						marker.addListener('gmp-click', () => {
							if (this.isInteractiveHotelMarker(markerInfo)) {
								this.toggleMarker(marker, markerInfo);
							}
						});

						this.mapMarkers.push(marker);

						bounds.extend(position);
					}
				});

				if (markersData.length > 1) {
					const padding = {
						top   : 50,
						right : 50,
						bottom: 50,
						left  : 50
					};
					map.fitBounds(bounds, padding);
				}


			},
			toggleMarker(marker, markerInfo) {
				if (this.activeMarker === marker) {
					return;
				}

				const mapContainer = this.getMapContainer();

				if (!mapContainer || !this.googleMap) {
					return;
				}

				const overlay = this.ensureMapOverlay(mapContainer);
				overlay.innerHTML = '';
				overlay.appendChild(this.buildMarkerDetailsPanel(markerInfo));
				overlay.classList.remove('hidden');

				this.activeMarker = marker;
				marker.zIndex = 9999;

				const position = marker.position || {
					lat: parseFloat(markerInfo.lat),
					lng: parseFloat(markerInfo.lng),
				};

				this.googleMap.panTo(position);
			},
			updatePoiInfo(poi) {
				if (!this.localMapInfo || !this.localMapInfo.length) {
					this.localMapInfo = Array.isArray(this.mapInfo) ? [...this.mapInfo] : [];
				}

				const key = `${poi.latitude},${poi.longitude}`;

				let additionalPoint = {
					lat    : parseFloat(poi.latitude),
					lng    : parseFloat(poi.longitude),
					title  : poi.name,
					content: poi.address || 'No address available',
					isPoi  : true,
				};

				const existingIndex = this.localMapInfo.findIndex(
					(point) => point.lat === additionalPoint.lat && point.lng === additionalPoint.lng
				);

				if (existingIndex === -1) {
					this.localMapInfo.push(additionalPoint);
					this.selectedPois = new Set([...this.selectedPois, key]);
				} else {
					this.localMapInfo.splice(existingIndex, 1);
					const updated = new Set(this.selectedPois);
					updated.delete(key);
					this.selectedPois = updated;
				}

				this.$nextTick(() => {
					this.initGoogleMap(this.divName);
				});
			},
			async getPoi() {
				try {
					const formData = new FormData();

					formData.append('lat', this.coordinates.lat);
					formData.append('lng', this.coordinates.lng);
					formData.append('radius', this.radius);

					const response = await fetch(this.baseUri + '/HotelEngine/poi', {
						method: 'POST',
						body  : formData,
					});

					if (!response.ok) {
						throw new Error(`Error fetching details: ${response.statusText}`);
					}

					this.pointsOfInterest = await response.json();
					this.showPoi = true;


				} catch (err) {
					console.error('Error fetching additional details:', err);
				}
			},
			showCategory(category) {
				this.activePoiCat = this.activePoiCat === category ? null : category;
			},
			selectAllInCategory(category, checked) {
				const pois = this.pointsOfInterest.pois[category];
				if (!pois) { return; }

				const updated = new Set(this.selectedPois);

				pois.forEach((poi) => {
					const key = `${poi.latitude},${poi.longitude}`;
					const existingIndex = this.localMapInfo.findIndex(
						(point) => point.lat === parseFloat(poi.latitude) && point.lng === parseFloat(poi.longitude)
					);

					if (checked && existingIndex === -1) {
						this.localMapInfo.push({
							lat    : parseFloat(poi.latitude),
							lng    : parseFloat(poi.longitude),
							title  : poi.name,
							content: poi.address || 'No address available',
							isPoi  : true,
						});
						updated.add(key);
					} else if (!checked && existingIndex !== -1) {
						this.localMapInfo.splice(existingIndex, 1);
						updated.delete(key);
					}
				});

				this.selectedPois = updated;
				this.showCategory(category);

				this.$nextTick(() => {
					this.initGoogleMap(this.divName);
				});
			},
			isPoiSelected(poi) {
				return this.selectedPois.has(`${poi.latitude},${poi.longitude}`);
			},
			getCategoryCheckState(category) {
				const pois = this.pointsOfInterest.pois?.[category] || [];
				const selectedCount = pois.filter((poi) => this.isPoiSelected(poi)).length;
				return {
					checked      : selectedCount === pois.length && pois.length > 0,
					indeterminate: selectedCount > 0 && selectedCount < pois.length,
				};
			}
		},
		watch: {
			selectedPois: {
				handler() {
					if (!this.pointsOfInterest.pois) {
						return;
					}

					this.$nextTick(() => {
						Object.keys(this.pointsOfInterest.pois).forEach((category) => {
							const ref = this.$refs['catCheck_' + category];
							const el = Array.isArray(ref) ? ref[0] : ref;
							if (el) {
								const state = this.getCategoryCheckState(category);
								el.indeterminate = state.indeterminate;
							}
						});
					});
				},
				deep: true,
			},
			showMap: {
				immediate: true,
				handler(newVal) {
					if (newVal) {
						// Ensure we have the initial data
						if (!this.localMapInfo || !this.localMapInfo.length) {
							this.localMapInfo = Array.isArray(this.mapInfo) ? [...this.mapInfo] : [];
						}
						this.$nextTick(() => {
							this.initGoogleMap(this.divName);
						});
					}
				}
			},
			mapInfo: {
				immediate: true,
				deep     : true,
				handler(newValue) {
					this.localMapInfo = Array.isArray(newValue) ? [...newValue] : [];

					// The showMap watcher builds the map the first time, so only redraw
					// here when an open map receives a new marker set (e.g. filtering).
					if (!this.showMap || !this.googleMap) {
						return;
					}

					this.$nextTick(() => {
						this.initGoogleMap(this.divName);
					});
				}
			},
		},
		mounted() {
			this.bindFullscreenListener();

			if (this.hasPoi) {
				this.getPoi();
			}
		},
		beforeUnmount() {
			this.unbindFullscreenListener();
		},
	};

	Adzuki.VueComponents.AdzukiGoogleMap = component;
}(jQuery, window.Adzuki || {}));
